import React from "react";
import PropTypes from "prop-types";
import { formatDateWithTime } from "../utils/formatters";
import { formatDate, sanitizeText } from "../utils/sanitize";
import { UI_CONSTANTS } from "../utils/constants";
import "./ConversationCard.css";

const ConversationCard = ({ conversation, onClick = null, onStatusChange = null }) => {
  const isClosed = conversation.status === "closed";

  const getFirstMessage = () => {
    if (!conversation.last_message) return "No messages yet";
    const sanitized = sanitizeText(conversation.last_message);
    return sanitized.length > UI_CONSTANTS.CONVERSATION_PREVIEW_MAX_LENGTH
      ? sanitized.substring(0, UI_CONSTANTS.CONVERSATION_PREVIEW_MAX_LENGTH) +
          "..."
      : sanitized;
  };

  const handleStatusToggle = (e) => {
    e.stopPropagation();
    if (onStatusChange) {
      onStatusChange(conversation, isClosed ? "active" : "closed");
    }
  };

  return (
    <div
      className={`conversation-card${isClosed ? " conversation-card--closed" : ""}`}
      onClick={() => onClick && onClick(conversation)}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if ((e.key === "Enter" || e.key === " ") && onClick) {
          onClick(conversation);
        }
      }}
      aria-label={`View conversation ${conversation.id?.toString().slice(-6) || "N/A"}`}
    >
      <div className="conversation-card-header">
        <div className="conversation-card-id">
          <span role="img" aria-label="Chat">
            💬
          </span>{" "}
          Conversation #
          {conversation.id
            ?.toString()
            .slice(-UI_CONSTANTS.CONVERSATION_ID_DISPLAY_LENGTH) || "N/A"}
          {isClosed && <span className="conversation-card-closed-badge">Closed</span>}
        </div>
        <div className="conversation-card-header-right">
          {onStatusChange && (
            <button
              className={`btn-close-conversation${isClosed ? " btn-reopen-conversation" : ""}`}
              onClick={handleStatusToggle}
              title={isClosed ? "Reopen conversation" : "Close conversation"}
              aria-label={isClosed ? "Reopen conversation" : "Close conversation"}
            >
              {isClosed ? "Reopen" : "Close"}
            </button>
          )}
          <div
            className="conversation-card-time"
            title={formatDateWithTime(conversation.started_at)}
          >
            {formatDate(conversation.started_at)}
          </div>
        </div>
      </div>

      <div className="conversation-card-preview">{getFirstMessage()}</div>

      <div className="conversation-card-footer">
        <div className="conversation-card-stat">
          <span className="stat-label">Messages:</span>
          <span className="stat-value">{conversation.message_count || 0}</span>
        </div>

        {conversation.session_id && (
          <div className="conversation-card-stat">
            <span className="stat-label">Session:</span>
            <span className="stat-value">
              {conversation.session_id.slice(
                -UI_CONSTANTS.SESSION_ID_DISPLAY_LENGTH,
              )}
            </span>
          </div>
        )}

        {conversation.total_tokens && (
          <div className="conversation-card-stat">
            <span className="stat-label">Tokens:</span>
            <span className="stat-value">
              {conversation.total_tokens.toLocaleString()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

ConversationCard.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    last_message: PropTypes.string,
    started_at: PropTypes.string,
    message_count: PropTypes.number,
    session_id: PropTypes.string,
    total_tokens: PropTypes.number,
    status: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
  onStatusChange: PropTypes.func,
};

export default ConversationCard;
